import React, { Component } from 'react'
import playButtonImg from './images/play-button.png'

/**
 * 分享落地页的播放器(TAPD 1006913)
 *
 * 为什么不复用 knowledge/components/videoPlayer.js:那两个都把 video.js 和埋点
 * mutation 绑死(还有模块级单例问题),而本页在 pigeon 取数、没有那些 mutation。
 * 这里要的东西也简单 —— 竖屏全屏播、进页静音自动播、点一下开声、可拖进度,
 * 原生 <video controls> 全都有,反而比 video.js 的皮肤在微信 X5 里更稳。
 *
 * ⚠️ 四个内联播放属性缺一不可,否则微信 X5 / iOS 会强制全屏接管:
 *   playsinline / webkit-playsinline / x5-playsinline / x5-video-player-type="h5-page"
 * (React 16 不认识带横杠的自定义属性首字母大写写法,必须用小写字符串形式,
 *  x5-* 这类未知属性 React 16 会原样透传到 DOM。)
 * 四个都用小写字符串形式,别再补 React 的驼峰 playsInline —— 它和 playsinline
 * 会被判成 duplicate props(编译告警),而且后者覆盖前者,留一个就够。
 */
export default class VideoPlayer extends Component {
  constructor(props) {
    super(props)
    this.state = {
      // 自动播是否被浏览器拦下 —— 拦了就露出大播放按钮(UI 稿的样子)
      blocked: false,
      muted: true,
      // 画面填充方式。先按 cover 起步(绝大多数是竖屏科普视频),拿到原生尺寸后再校正,
      // 见 onLoadedMetadata。读不到尺寸就一直是 cover —— 宁可铺满,不要平白留黑边。
      objectFit: 'cover',
    }
    this.videoRef = React.createRef()
  }

  componentDidMount() {
    const video = this.videoRef.current
    if (!video) return
    // ⚠️ 手动补 muted **属性**(2026-09-10):React 把 muted 当 property 设,DOM 元素上
    // 并没有 muted 这个属性(实测 v.muted === true 但 v.hasAttribute('muted') === false)。
    // 桌面 Chrome 在 play() 时读的是 property,所以照样自动播;而 **iOS Safari 与微信 X5
    // 判定"能不能静音自动播"读的是属性**,读不到就当成"带声音的自动播"拦下来 ——
    // 表现就是桌面一切正常、手机上进页是暂停的。JSX 上那个 muted 不能删,它负责真静音。
    video.setAttribute('muted', '')
    this.tryAutoPlay()
    this.bindWechatAutoPlay()
    // 元数据可能永远不来(iOS 微信),用封面的比例先把填充方式定下来
    this.probePoster()
  }

  componentWillUnmount() {
    this.unmounted = true
    if (this.posterProbe) {
      this.posterProbe.onload = null
      this.posterProbe.onerror = null
    }
    if (this.onBridgeReady) {
      document.removeEventListener('WeixinJSBridgeReady', this.onBridgeReady, false)
      this.onBridgeReady = null
    }
  }

  /**
   * 试一次静音自动播。iOS/安卓都只允许静音自动播;play() 返回 Promise 的浏览器上
   * 被拦会 reject,老浏览器不返回 Promise,靠 onPlay 事件把 blocked 复位。
   *
   * 可以被调用多次(挂载时一次、微信桥就绪后再一次),已经在播就直接返回。
   */
  tryAutoPlay = () => {
    const video = this.videoRef.current
    if (!video || !video.paused) return
    const played = video.play()
    if (played && typeof played.catch === 'function') {
      played.catch(() => {
        if (!this.unmounted) this.setState({ blocked: true })
      })
    }
  }

  /**
   * 微信里再试一次自动播(2026-09-10 真机实证)。
   *
   * iOS 微信(WKWebView)在 JS-SDK 桥就绪之前既不播、也**不加载任何媒体数据** ——
   * 真机读数是 `readyState 0` / `videoWidth 0×0`,连元数据都没有。这跟普通浏览器的
   * "自动播被策略拦下"不是一回事:那种情况至少 preload 会把元数据取回来。
   * `WeixinJSBridgeReady` 是微信特有的时机信号,桥好了之后 play() 才有机会成功。
   *
   * ⚠️ 桥可能在本组件挂载**之前**就已就绪 —— 那时事件早就发过了、不会再发一次,
   * 所以必须先查 window.WeixinJSBridge 是否已存在,只挂监听会永远等不到。
   *
   * 非微信环境下 window.WeixinJSBridge 不存在、事件也永远不来,这段等于没有,
   * 不需要额外做 UA 判断。
   */
  bindWechatAutoPlay = () => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return
    if (window.WeixinJSBridge) {
      this.tryAutoPlay()
      return
    }
    this.onBridgeReady = () => this.tryAutoPlay()
    document.addEventListener('WeixinJSBridgeReady', this.onBridgeReady, false)
  }

  /**
   * 按原生宽高决定填充方式,**口径与 App 完全一致**
   * (needle2020 的 feedV2/PlayerView.js `useVideoResizeMode`:
   *  `naturalSize.width >= naturalSize.height ? 'contain' : 'cover'`)。
   *
   * 横屏源必须 contain:1280×720 放进 390×844 用 cover 要放大到 1500×844,
   * **只剩正中 26% 可见**,两边各裁掉一大半(2026-09-10 实测)。
   * 竖屏维持 cover 铺满(标准 9:16 用 contain 会上下留黑边,与 UI 稿不符)。
   *
   * @param source 'video' | 'poster' —— 视频自身的尺寸是权威,封面只是替补,见 probePoster
   */
  applyAspect = (width, height, source) => {
    if (!width || !height) return
    // 视频尺寸一旦到手就不再让封面改口(封面可能裁过、比例未必与视频一致)
    if (this.sizeSource === 'video' && source !== 'video') return
    this.sizeSource = source
    this.setState({ objectFit: width >= height ? 'contain' : 'cover' })
  }

  onLoadedMetadata = () => {
    const video = this.videoRef.current
    if (!video) return
    this.applyAspect(video.videoWidth, video.videoHeight, 'video')
  }

  /**
   * 用**封面图**的宽高比兜底(2026-09-10 真机发现)。
   *
   * iOS 微信(WKWebView)完全无视 `preload="auto"`:用户点之前一个字节的媒体数据都不加载,
   * 真机读数是 `readyState 0` / `videoWidth 0×0`。于是 loadedmetadata 从不触发,
   * 填充方式一直停在默认的 cover —— 横屏视频的封面就被裁成"短边充满"。
   *
   * 封面图(<video poster>)却是照常加载的,而且它就是这条视频的封面,比例一致
   * (实测这条:封面 1280×720,视频 1280×720)。所以拿它当早期信号,首屏就能给对。
   * 视频元数据真到了以后由 applyAspect 覆盖过去,封面不再有发言权。
   *
   * ⚠️ object-fit 同样作用于 poster,所以这个兜底修的正是"还没点播放时看到的那一屏"。
   */
  probePoster = () => {
    const { poster } = this.props
    if (!poster || typeof window === 'undefined' || !window.Image) return
    const probe = new window.Image()
    this.posterProbe = probe
    probe.onload = () => {
      if (this.unmounted) return
      this.applyAspect(probe.naturalWidth, probe.naturalHeight, 'poster')
    }
    // 封面拉不到就维持 cover,不做别的
    probe.onerror = () => {}
    probe.src = poster
  }

  // 点画面开声音。**只在静音期间盖着**,开声后这层就撤掉,播放/暂停/拖进度
  // 全部交回原生控件 —— 需求(三·(四))只要「自动播(静音)+ 点击开声音 + 支持进度拖动」,
  // 并没有要求"点画面暂停"。之前那层常驻的全屏点击区会和 <video controls> 打架:
  // 点控件条的 click 会不会冒泡到外层各浏览器行为不一,冒泡就意味着拖完进度
  // 顺手被暂停一次。撤掉常驻层,这个冲突就不存在了,不用赌浏览器实现。
  onUnmute = () => {
    const video = this.videoRef.current
    if (!video) return
    video.muted = false
    this.setState({ muted: false })
    if (video.paused) {
      const played = video.play()
      if (played && typeof played.catch === 'function') played.catch(() => {})
    }
    this.props.onUnmute && this.props.onUnmute()
  }

  onManualPlay = () => {
    const video = this.videoRef.current
    if (!video) return
    video.muted = false
    this.setState({ blocked: false, muted: false })
    const played = video.play()
    if (played && typeof played.catch === 'function') played.catch(() => {})
    this.props.onManualPlay && this.props.onManualPlay()
    // 这一下同样把声音打开了,埋点要跟"点开声音层"记成一件事,别漏报
    this.props.onUnmute && this.props.onUnmute()
  }

  render() {
    const { src, poster } = this.props
    const { blocked, muted, objectFit } = this.state
    return (
      <div style={styles.wrap}>
        <video
          ref={this.videoRef}
          src={src}
          poster={poster}
          // 自动播被拦时关掉原生控件(TAPD 1006913,2026-09-10 真机发现):控件条自带一个
          // 播放键,而下面 blocked 分支又叠了自绘的大白圈,画面上就是**两个**播放图标。
          // 桌面复现不了 —— 静音自动播在桌面一定成功,blocked 恒为 false,大白圈不渲染。
          // 留自绘那个:它是 UI 稿的样子,也比控件条角落那个小键更像个可点的东西。
          // 用户点一下 → onManualPlay 把 blocked 复位 → 原生控件立刻回来,拖进度不受影响。
          controls={!blocked}
          muted
          autoPlay
          loop
          preload="auto"
          playsinline="true"
          webkit-playsinline="true"
          x5-playsinline="true"
          x5-video-player-type="h5-page"
          style={{ ...styles.video, objectFit }}
          onLoadedMetadata={this.onLoadedMetadata}
          onPlay={() => blocked && this.setState({ blocked: false })}
        />
        {blocked && (
          // 微信内自动播是做不到的(2026-09-11 真机确认:iOS 微信 readyState 一直 0,
          // 连 WeixinJSBridgeReady 之后也不给加载),所以这个按钮不是兜底、是**主交互**。
          // 图用设计切图(视频-3 画板的「形状结合 2」,140×140 设计单位 ÷ density 2 = 70pt),
          // 不再自绘 SVG —— 之前那版的圆环粗细和三角比例都是我照着描的,跟设计对不上。
          <div style={styles.playMask} onClick={this.onManualPlay}>
            <img src={playButtonImg} alt="播放" style={styles.playButton} />
          </div>
        )}

        {/* 开声音层:只在静音期间存在,且**底部留出控件条**不挡拖进度;
            点一下开声后整层撤掉,画面全部交回原生控件。自动播被拦时上面那层已经
            盖住了(点它同样会开声),这里就不重复叠。 */}
        {!blocked && muted && (
          <div style={styles.unmuteLayer} onClick={this.onUnmute}>
            <div style={styles.unmuteTip}>
              {/* 用内联 SVG 不用 emoji:emoji 在各安卓机型上字形差异大,还会触发无障碍告警 */}
              <svg width="14" height="14" viewBox="0 0 24 24" style={styles.unmuteIcon} aria-hidden="true">
                <path d="M4 9v6h4l5 4V5L8 9H4z" fill="#FFFFFF" />
                <path d="M16 8l6 6M22 8l-6 6" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" fill="none" />
              </svg>
              <span>点击开启声音</span>
            </div>
          </div>
        )}
      </div>
    )
  }
}

const styles = {
  wrap: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: '#000000',
  },
  video: {
    width: '100%',
    height: '100%',
    // ⚠️ objectFit 不在这里定,由 state 在 onLoadedMetadata 里按原生宽高算(见那里的注释)。
    // 竖屏 cover / 横屏 contain,与 App 同一条规则。
    // 竖屏之所以用 cover:标准 9:16(1080×1920)放进全面屏(iPhone 12 Pro 是 390×844,
    // 比 9:16 更窄长)用 contain 会上下各留 ~56px 黑边,与 UI 稿"视频铺满整屏"不符;
    // cover 左右各裁约 10%,人物居中的科普视频没影响。
    background: '#000000',
  },
  playMask: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // 70 = 设计稿 140 ÷ density 2。用的是 @3x 切图(420px)缩到 70,3x 屏上也锐。
  playButton: {
    width: 70,
    height: 70,
    display: 'block',
  },
  // 开声音层。bottom 留 64:原生控件条大约 40~50 高(iOS 更高一些),
  // 空出来让用户能直接拖进度,不用先点掉这层。
  unmuteLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 64,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: 72,
  },
  unmuteTip: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    padding: '0 14px',
    borderRadius: 16,
    background: 'rgba(0,0,0,0.45)',
    color: '#FFFFFF',
    fontSize: 13,
  },
  unmuteIcon: {
    marginRight: 6,
    fontSize: 14,
  },
}



// WEBPACK FOOTER //
// ./src/modules/videoShare/components/videoPlayer.js